import React, { createContext, useState, useContext, useRef } from 'react';
import { showSweetAlert, translations } from './Utilities';
import { usePlayAudio } from './PlayAudio';

const RecordUserContext = createContext();

export const useRecordUser = () => useContext(RecordUserContext);

export const RecordUserProvider = ({ children }) => {
  const [recording, setRecording] = useState(false);
  const processingSpeech = useRef(false);
  const [transcription, setTranscription] = useState('');
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceStartTimeRef = useRef(null);
  const streamRef = useRef(null);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const { stopAudio} = usePlayAudio(); 
  const silenceThreshold = 0.005; // RMS threshold to detect silence
  const silenceGracePeriod = 3000; // Grace period of continuous silence before stopping (in milliseconds)
  const autoStopDuration = 20000; 

   const initializeAudioContext = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 2048;
        analyserRef.current = analyser;
        audioContextRef.current = audioContext;
        streamRef.current = stream;

        source.connect(analyser);
        setReady(true);
        console.log('Audio context and media stream initialized.');
      } catch (error) {
        setError(`Error initializing audio context: ${error.message}`);
        console.error('Error initializing audio context:', error);
      }
  };

  const startRecording = async (selectedLanguage, originText) => {       
    if (processingSpeech.current) { // Prevent starting a new recording if one is already in progress
      return; 
    } 
    console.log("processing ..." );

    if (!ready ) {
      await initializeAudioContext();
      setSelectedLanguage(selectedLanguage);
    }

    try {
      console.time('Total Recording Time');
      resetState();

      const stream = streamRef.current;      
      if (!stream) {
        throw new Error('MediaStream is not available');
      }

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.start();  
      setRecording(true);    
      processingSpeech.current = true;
      console.log('Recording started.');

      clearInterval(silenceTimerRef.current);   // in case user never stop recording after starting
      clearTimeout(autoStopTimerRef.current);
      silenceTimerRef.current = setInterval(checkSilence, 100);
      autoStopTimerRef.current = setTimeout(stopRecording, autoStopDuration);

      mediaRecorder.onstop = async () => {
        console.time('Blob Creation Time');
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        audioChunksRef.current = [];
        console.timeEnd('Blob Creation Time');
        if (processingSpeech.current) {
          await sendAudioToServer(audioBlob, originText);
        } else {
          console.log('Recording stopped before completion; not sending to server.'); // this happens when user goes to another page while recording
        }
      };
    } catch (error) {
      setError(`Error starting recording: ${error.message}`);
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    setRecording(false);
    clearInterval(silenceTimerRef.current);
    clearTimeout(autoStopTimerRef.current);
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    console.timeEnd('Total Recording Time');
    console.log('Recording stopped.');
  };
  
  const resetState = () => {
    clearInterval(silenceTimerRef.current);
    clearTimeout(autoStopTimerRef.current);
    audioChunksRef.current = [];
    silenceStartTimeRef.current = null;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    console.log('State reset.');
  };

  const checkSilence = () => {
    const analyser = analyserRef.current;
    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteTimeDomainData(dataArray);

    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      const x = dataArray[i] / 128.0 - 1.0;
      sum += x * x;
    }
    const rms = Math.sqrt(sum / bufferLength);

    if (rms < silenceThreshold) {
      if (!silenceStartTimeRef.current) {
        silenceStartTimeRef.current = Date.now();
      } else if (Date.now() - silenceStartTimeRef.current > silenceGracePeriod) {
        showSweetAlert(translations[selectedLanguage].SpeakLouder, 'practice-message');
        stopRecording();
        console.log('Silence detected, stopping recording.');
      }
    } else {
      silenceStartTimeRef.current = null;
    }
  };
 
  const sendAudioToServer = async (audioBlob) => {
    console.time('Uploading Audio');
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.wav');    
    console.log('Sending audio to server...');
    console.log('Blob size:', audioBlob.size);

    try {
      console.time('Server Round-Trip Time');
      console.log('Backend server:', process.env.REACT_APP_PY_SERVER);
      // const response = await fetch('http://localhost:5000/upload-audio', {
      const response = await fetch(`${process.env.REACT_APP_PY_SERVER}/recognize-audio`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upload audio. Server responded with: ${errorText}`);
      }
      
      if (!processingSpeech.current) {
          console.log('Upload canceled.');
          return;
        }

      const jsonResponse = await response.json();      

      if (processingSpeech.current) {   // if user navigate away while waiting for feedback
        console.timeEnd('Server Round-Trip Time');
        console.timeEnd('Uploading Audio');

        if (jsonResponse.transcription && jsonResponse.transcription !== '') {
          setTranscription(jsonResponse.transcription || JSON.stringify(jsonResponse));
        } else {
          showSweetAlert(translations[selectedLanguage].SpeakLouder, 'practice-message');
        }
        processingSpeech.current = false;
      }
    } catch (error) {
      console.timeEnd('Server Round-Trip Time');
      console.timeEnd('Uploading Audio');
      console.error('Error uploading audio:', error);
      setError(`Error uploading audio: ${error.message}`);
    }
  };

  // this function is to stop audio, stop sending speech to server, stop recording, and remove red button on top of browser tap
  const stopAudioRecordingRedbutton = () => {    
    stopAudio();  

    processingSpeech.current = false;

   if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().then(() => {
        audioContextRef.current = null; // Reset the ref
      }).catch((error) => {
        console.error("Error closing AudioContext:", error);
      });
    } else {
      audioContextRef.current = null; // If close is unsupported, reset anyway
    }
    
    if (mediaRecorderRef.current) {
      stopRecording();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      mediaRecorderRef.current = null; // Reset the ref
    }

    setReady(false);
};

  return (
    <RecordUserContext.Provider value={{ processingSpeech, setTranscription, recording, transcription, error, startRecording, stopRecording, stopAudioRecordingRedbutton, ready }}>
      {children}
    </RecordUserContext.Provider>
  );
};